import React from 'react'
import { Box, Button, CircularProgress, Paper, Typography } from '@material-ui/core'
import i18n from 'common/util/internationalization'
import {
    MAX_TEMPLATE_TITLE_LENGTH,
    MAX_TEMPLATE_DESCRIPTION_LENGTH
} from 'common/constants/constants'

import { TEMPLATE_UPLOAD_STATUS } from '../../../src/components/minutetaker/components/InvitationView/types'

// Icons
import { IconButton } from 'components/shared/StyledComponents'
import DownloadIcon from 'assets/icons/DownloadIcon'
import UploadIcon from 'assets/icons/UploadIcon'
import ErrorIcon from 'assets/icons/ErrorIcon'

//Components
import { NewDialogTextField } from 'components/shared/StyledComponents'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'
import TextLengthCounter from 'components/shared/TextLengthCounter'

//Styles
import {
    UploadContainerTemplate,
    UploadInput,
    UploadText,
    UploadLink,
    UploadInfo,
    UploadSection,
    UploadIconStyle,
    UploadFileDetails,
    HighlightedText,
    StyledDeleteSectionIcon,
    StyledSuccessIcon,
    UploadFileSize,
    UploadFileError
} from '../../components/minutetaker/TranscriptToMinutes/UploadStyles'
import TemplateDocumentIcon from 'assets/icons/TemplateDocumentIcon'
import { formatDate } from 'common/formatters/date'
import { useStyles } from './templateDrawerStyles'

interface FileItem {
    name: string
    size: number
    file: File | null
}

interface UploadStatusProps {
    uploadStatus: TEMPLATE_UPLOAD_STATUS | null | undefined
    items: FileItem | null | undefined
    fileUploadError: string | null | undefined
    validationErrors?: string[]
    isUpdateTemplateClicked: boolean | undefined
    title: string
    description: string
    templateList: any
    returnTitleErrorMessage: string | null
    returnDescriptionErrorMessage: string | null
    onTitleChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onDescriptionChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onDrop: (e: React.DragEvent) => void
    onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
    onResetUpload: () => void
    onDownloadTemplate: ((id: string, fileName: string) => void) | undefined
    renderSystemTemplateText: (showTitle: boolean) => React.ReactElement
    headerTitle: string
}

const getUploadErrorMessage = (status: TEMPLATE_UPLOAD_STATUS, error: string | null) => {
    if (error) return error
    const messages = {
        [TEMPLATE_UPLOAD_STATUS.FILE_NAME_LENGTH]: 'AI_TTM_FILE_NAME_LENGTH_ERROR',
        [TEMPLATE_UPLOAD_STATUS.FILE_SIZE_LIMIT]: 'MINUTES_TEMPLATES_MAX_FILE_SIZE_ERROR',
        [TEMPLATE_UPLOAD_STATUS.EMPTY_FILE]: 'MINUTES_TEMPLATES_MISSING_REQUIRED_PLACEHOLDER',
        [TEMPLATE_UPLOAD_STATUS.HTML_CONTENT]: 'NO_HTML_MESSAGE',
        [TEMPLATE_UPLOAD_STATUS.SAVE_ERROR]: 'MINUTES_TEMPLATES_AT_LEAST_ONE_SUPPORTED_TAG_REQUIRED'
    }
    return i18n.t(messages[status] || 'NETWORK_ERROR')
}

const isErroredStatus = (status: TEMPLATE_UPLOAD_STATUS) =>
    [
        TEMPLATE_UPLOAD_STATUS.FAILED,
        TEMPLATE_UPLOAD_STATUS.UNSUPPORTED_FORMAT,
        TEMPLATE_UPLOAD_STATUS.FILE_NAME_LENGTH,
        TEMPLATE_UPLOAD_STATUS.EMPTY_FILE,
        TEMPLATE_UPLOAD_STATUS.HTML_CONTENT,
        TEMPLATE_UPLOAD_STATUS.FILE_SIZE_LIMIT,
        TEMPLATE_UPLOAD_STATUS.SAVE_ERROR
    ].includes(status)

const renderFileSize = (size: number) =>
    size < 1048576 ? `${(size / 1024).toFixed(2)} KB` : `${(size / 1048576).toFixed(2)} MB`

export const UploadStatus: React.FC<UploadStatusProps> = ({
    uploadStatus,
    items,
    fileUploadError,
    validationErrors,
    isUpdateTemplateClicked,
    title,
    description,
    templateList,
    returnTitleErrorMessage,
    returnDescriptionErrorMessage,
    onTitleChange,
    onDescriptionChange,
    onDrop,
    onFileSelect,
    onResetUpload,
    onDownloadTemplate,
    renderSystemTemplateText,
    headerTitle
}) => {
    const classes = useStyles()

    if (uploadStatus === TEMPLATE_UPLOAD_STATUS.DEFAULT && !isUpdateTemplateClicked) {
        return (
            <>
                <UploadContainerTemplate
                    data-analytics="MM-Template-Upload"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onDrop}
                >
                    <UploadInput
                        type="file"
                        accept=".docx"
                        onChange={onFileSelect}
                        id="fileUpload"
                    />
                    <UploadText>
                        <UploadIconStyle>
                            <UploadIcon />
                        </UploadIconStyle>
                        <label
                            htmlFor="fileUpload"
                            style={{ cursor: 'pointer', display: 'inline-block' }}
                            tabIndex={0}
                            onKeyDown={(e) =>
                                ['Enter', ' '].includes(e.key) &&
                                document.getElementById('fileUpload')?.click()
                            }
                        >
                            <UploadLink as="span">{i18n.t('AI_TTM_UPLOAD_DOC')}</UploadLink>
                        </label>
                        | <span>{i18n.t('AI_TTM_DRAG_DROP')}</span>
                    </UploadText>
                </UploadContainerTemplate>
                <UploadInfo>
                    {i18n.t('MINUTES_TEMPLATES_FORMAT_SUPPORTED')}
                    <br />
                    {i18n.t('MINUTES_TEMPLATES_MAX_FILE_SIZE')}
                </UploadInfo>
            </>
        )
    } else {
        return (
            <>
                {!isUpdateTemplateClicked && (
                    <div className={classes.uploadStatus}>
                        <UploadSection>
                            {uploadStatus === TEMPLATE_UPLOAD_STATUS.PROGRESS && (
                                <CircularProgress size={34} thickness={4} color="primary" />
                            )}
                            {uploadStatus === TEMPLATE_UPLOAD_STATUS.SUCCESS && (
                                <StyledSuccessIcon />
                            )}

                            <UploadFileDetails>
                                <HighlightedText>{items?.name}</HighlightedText>
                                {uploadStatus === TEMPLATE_UPLOAD_STATUS.SUCCESS && items?.size && (
                                    <UploadFileSize>{renderFileSize(items.size)}</UploadFileSize>
                                )}
                                {uploadStatus && isErroredStatus(uploadStatus) && (
                                    <>
                                        <div className={classes.errorRow}>
                                            <div className={classes.errorIconBox}>
                                                {uploadStatus && isErroredStatus(uploadStatus) && (
                                                    <ErrorIcon />
                                                )}
                                            </div>
                                            <UploadFileError>
                                                {uploadStatus ===
                                                TEMPLATE_UPLOAD_STATUS.UNSUPPORTED_FORMAT
                                                    ? i18n.t('MINUTES_TEMPLATES_UNSUPPORTED_FILE')
                                                    : getUploadErrorMessage(
                                                          uploadStatus,
                                                          fileUploadError || null
                                                      )}
                                            </UploadFileError>
                                        </div>

                                        <UploadFileError>
                                            {validationErrors && validationErrors.length > 0 && (
                                                <ul className={classes.errorList}>
                                                    {validationErrors.map((err, idx) => (
                                                        <li key={idx}>{err}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </UploadFileError>
                                    </>
                                )}
                            </UploadFileDetails>

                            {(uploadStatus === TEMPLATE_UPLOAD_STATUS.SUCCESS ||
                                (uploadStatus && isErroredStatus(uploadStatus))) && (
                                <AtlasTooltip title={i18n.t('DELETE') as string} placement="bottom">
                                    <IconButton
                                        tabIndex={0}
                                        disableFocusRipple
                                        disableRipple
                                        className={classes.myIconButton}
                                        data-analytics="MM-Template-DeleteFile"
                                        onClick={onResetUpload}
                                    >
                                        <StyledDeleteSectionIcon />
                                    </IconButton>
                                </AtlasTooltip>
                            )}
                        </UploadSection>
                    </div>
                )}
                {(uploadStatus === TEMPLATE_UPLOAD_STATUS.SUCCESS || isUpdateTemplateClicked) && (
                    <div className={classes.templateDetails}>
                        <div className={classes.fieldWrapper}>
                            <Typography className={classes.inputLabel}>
                                {isUpdateTemplateClicked
                                    ? i18n.t('MEETING_NAME')
                                    : i18n.t('MINUTES_TEMPLATES_TEMPLATE_TITLE')}
                            </Typography>

                            <NewDialogTextField
                                variant="outlined"
                                placeholder={i18n.t('MINUTES_TEMPLATES_ENTER_TITLE_PLACEHOLDER')}
                                value={title}
                                onChange={onTitleChange}
                                className={classes.textField}
                                inputProps={{ maxLength: 1000 }}
                                error={!!returnTitleErrorMessage}
                                helperText={
                                    <TextLengthCounter
                                        maxValue={MAX_TEMPLATE_TITLE_LENGTH}
                                        value={title}
                                        colour={true}
                                        errorText={returnTitleErrorMessage}
                                    />
                                }
                            />
                        </div>
                        <div className={classes.fieldWrapper}>
                            <Typography className={classes.inputLabel}>
                                {i18n.t('MINUTES_TEMPLATES_TEMPLATE_DESCRIPTION')}
                            </Typography>
                            <NewDialogTextField
                                variant="outlined"
                                placeholder={i18n.t(
                                    'MINUTES_TEMPLATES_ENTER_DESCRIPTION_PLACEHOLDER'
                                )}
                                value={description}
                                onChange={onDescriptionChange}
                                multiline
                                rows={6}
                                className={classes.textField}
                                error={!!returnDescriptionErrorMessage}
                                InputProps={{
                                    style: {
                                        paddingTop: '8px',
                                        paddingBottom: '8px'
                                    }
                                }}
                                helperText={
                                    <TextLengthCounter
                                        errorText={
                                            returnDescriptionErrorMessage ===
                                            'MISSING_REQUIRED_FIELDS'
                                                ? i18n.t('MISSING_REQUIRED_FIELDS')
                                                : returnDescriptionErrorMessage ===
                                                  i18n.t('MINUTES_TEMPLATES_DESCRIPTION_MAX_LENGTH')
                                                ? i18n.t('MINUTES_TEMPLATES_DESCRIPTION_MAX_LENGTH')
                                                : returnDescriptionErrorMessage ===
                                                  i18n.t('NO_HTML_MESSAGE')
                                                ? i18n.t('NO_HTML_MESSAGE')
                                                : ''
                                        }
                                        maxValue={MAX_TEMPLATE_DESCRIPTION_LENGTH}
                                        value={description}
                                        colour={true}
                                    />
                                }
                            />
                        </div>
                    </div>
                )}
                {isUpdateTemplateClicked && (
                    <div>
                        <div className={classes.templateInfoContainer}>
                            <div className={classes.templateInfoBox}>
                                <Typography
                                    variant="subtitle2"
                                    className={classes.templateInfoTitle}
                                >
                                    {i18n.t('DETAILS')}
                                </Typography>
                                <Typography variant="body2" className={classes.templateInfoText}>
                                    {i18n.t('MINUTES_TEMPLATES_UPLOADED_BY')}:{' '}
                                    {templateList?.displayCreatedBy || '—'}
                                </Typography>
                                <Typography variant="body2" className={classes.templateInfoText}>
                                    {i18n.t('MINUTES_TEMPLATES_UPLOADED_DATE')}:{' '}
                                    {formatDate({ date: templateList.createdAt })}
                                </Typography>
                            </div>
                            {templateList?.displayUpdatedBy !== 'Unknown' && (
                                <div className={classes.templateInfoBox}>
                                    <Typography
                                        variant="subtitle2"
                                        className={classes.templateInfoTitle}
                                    >
                                        {i18n.t('MINUTES_TEMPLATES_LAST_MODIFIED')}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={classes.templateInfoText}
                                    >
                                        {i18n.t('MINUTES_TEMPLATES_MODIFIED_BY')}:{' '}
                                        {templateList?.displayUpdatedBy}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={classes.templateInfoText}
                                    >
                                        {i18n.t('MINUTES_TEMPLATES_DATE_OF_MODIFIED')}:{' '}
                                        {formatDate({ date: templateList.updatedAt })}
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {isUpdateTemplateClicked && (
                    <Box className={classes.rootContainer}>
                        <Paper className={classes.root} elevation={0}>
                            <Box className={classes.iconBox}>
                                <Box className={classes.logo}>{<TemplateDocumentIcon />}</Box>
                                <Box className={classes.textBox}>
                                    <Typography className={classes.title}>
                                        {renderSystemTemplateText(true)}
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {renderSystemTemplateText(false)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className={classes.rightSection}>
                                <Button
                                    data-analytics="MM-Template-Download"
                                    className={classes.downloadButton}
                                    color="primary"
                                    onClick={() =>
                                        onDownloadTemplate?.(templateList?.id, headerTitle)
                                    }
                                    disableFocusRipple
                                    disableRipple
                                >
                                    <DownloadIcon />
                                    {i18n.t('MINUTES_TEMPLATES_DOWNLOAD_TEMPLATE')}
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                )}
            </>
        )
    }
}
